if (!customElements.get('adp-before-after-carousel')) {
  class AdpBeforeAfterCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.track = this.querySelector('[data-adp-track]');
      this.viewport = this.querySelector('[data-adp-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-adp-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-adp-dot]'));
      this.previousButton = this.querySelector('[data-adp-previous]');
      this.nextButton = this.querySelector('[data-adp-next]');
      this.autoplayButton = this.querySelector('[data-adp-autoplay]');
      this.autoplayLabel = this.querySelector('[data-adp-autoplay-label]');
      this.autoplayIcon = this.querySelector('[data-adp-autoplay-icon]');
      this.currentCounter = this.querySelector('[data-adp-current]');
      this.status = this.querySelector('[data-adp-status]');

      if (!this.track || this.slides.length === 0) return;

      this.index = 0;
      this.pointerStartX = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.delay = Math.max(Number(this.dataset.autoplayDelay) || 7000, 5000);
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.timer = null;

      this.previousButton?.addEventListener('click', () => this.goTo(this.index - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1, true));
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay());
      this.dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => this.goTo(dotIndex, true));
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.scheduleAutoplay();
      });
      this.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.scheduleAutoplay();
      });
      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.scheduleAutoplay();
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.focusPaused = false;
          this.scheduleAutoplay();
        }
      });

      this.viewport?.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport?.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport?.addEventListener('pointercancel', () => {
        this.pointerStartX = null;
      });

      this.onVisibilityChange = () => this.scheduleAutoplay();
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.onBlockSelect = (event) => {
        const selectedSlide = event.target.closest?.('[data-adp-slide]');
        if (!selectedSlide || !this.contains(selectedSlide)) return;
        const selectedIndex = this.slides.indexOf(selectedSlide);
        if (selectedIndex < 0) return;
        this.userPaused = true;
        this.updateAutoplayControl();
        this.goTo(selectedIndex, false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.updateAutoplayControl();
      this.goTo(0, false);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onKeydown(event) {
      if (this.slides.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1, true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, true);
      }
    }

    onPointerDown(event) {
      if (!event.isPrimary || this.slides.length < 2) return;
      this.pointerStartX = event.clientX;
    }

    onPointerUp(event) {
      if (this.pointerStartX === null || !event.isPrimary) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;

      if (Math.abs(distance) < 45) return;
      this.goTo(distance > 0 ? this.index - 1 : this.index + 1, true);
    }

    goTo(nextIndex, announce) {
      const slideCount = this.slides.length;
      this.index = (nextIndex + slideCount) % slideCount;
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', String(slideIndex !== this.index));
      });
      this.dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', String(dotIndex === this.index));
      });

      if (this.currentCounter) this.currentCounter.textContent = String(this.index + 1);
      if (announce && this.status) {
        const title = this.slides[this.index].dataset.slideTitle;
        this.status.textContent = `Dia ${this.index + 1} van ${slideCount}${title ? `: ${title}` : ''}`;
      }

      this.scheduleAutoplay();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.updateAutoplayControl();
      this.scheduleAutoplay();
    }

    updateAutoplayControl() {
      if (!this.autoplayButton) return;
      this.autoplayButton.setAttribute('aria-pressed', String(this.userPaused));
      if (this.autoplayLabel) this.autoplayLabel.textContent = this.userPaused ? 'Afspelen' : 'Pauzeren';
      if (this.autoplayIcon) this.autoplayIcon.textContent = this.userPaused ? '▶' : 'Ⅱ';
      this.autoplayButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Automatisch afspelen starten' : 'Automatisch afspelen pauzeren'
      );
    }

    scheduleAutoplay() {
      window.clearTimeout(this.timer);
      if (
        !this.autoplayEnabled ||
        this.userPaused ||
        this.hoverPaused ||
        this.focusPaused ||
        document.hidden
      ) {
        return;
      }

      this.timer = window.setTimeout(() => this.goTo(this.index + 1, false), this.delay);
    }
  }

  customElements.define('adp-before-after-carousel', AdpBeforeAfterCarousel);
}
